import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SITE_URL, findPage, languagePath, normalizeLanguage, pages } from './routes';

const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const DEFAULT_IMAGE = '/android-chrome-512x512.png';
const absoluteUrl = (path) => `${SITE_URL}${path}`;

export function createStructuredData(page, language) {
  if (!page) return null;
  const lang = normalizeLanguage(language);
  const code = lang === 'en' ? 'en' : 'sv';
  const url = absoluteUrl(languagePath(page, lang));
  const pageId = `${url}#webpage`;
  const image = absoluteUrl(page.image || DEFAULT_IMAGE);
  const graph = [
    {
      '@type': 'Organization', '@id': ORGANIZATION_ID,
      name: 'THAS Multitech', url: `${SITE_URL}/`,
      logo: absoluteUrl(DEFAULT_IMAGE),
      email: 'info@thas-multitech.com', telephone: '+46760211685',
      location: { '@type': 'Place', name: 'Stockholm, Sweden' },
    },
    {
      '@type': 'WebSite', '@id': WEBSITE_ID,
      url: `${SITE_URL}/`, name: 'THAS Multitech',
      publisher: { '@id': ORGANIZATION_ID }, inLanguage: ['sv', 'en'],
    },
    {
      '@type': page.type === 'collection' ? 'CollectionPage' : 'WebPage',
      '@id': pageId, url, name: page.title[lang], description: page.description[lang],
      inLanguage: code, isPartOf: { '@id': WEBSITE_ID },
      ...(page.type === 'service' ? { mainEntity: { '@id': `${url}#service` } } : {}),
      ...(page.type === 'article' ? { mainEntity: { '@id': `${url}#article` } } : {}),
      ...(page.type !== 'home' ? { breadcrumb: { '@id': `${url}#breadcrumb` } } : {}),
    },
  ];
  if (page.type === 'service') {
    graph.push({
      '@type': 'Service', '@id': `${url}#service`,
      name: page.name[lang], serviceType: page.name[lang],
      description: page.description[lang], url,
      provider: { '@id': ORGANIZATION_ID }, mainEntityOfPage: { '@id': pageId },
    });
  }
  if (page.type === 'article') {
    graph.push({
      '@type': 'BlogPosting', '@id': `${url}#article`,
      headline: page.name[lang], description: page.description[lang],
      url, inLanguage: code, image,
      publisher: { '@id': ORGANIZATION_ID }, mainEntityOfPage: { '@id': pageId },
    });
  }
  if (page.type !== 'home') {
    const home = pages.find((item) => item.type === 'home');
    const ancestors = [home];
    if (page.type === 'article') ancestors.push(pages.find((item) => item.type === 'collection'));
    graph.push({
      '@type': 'BreadcrumbList', '@id': `${url}#breadcrumb`,
      itemListElement: [...ancestors, page].map((item, index) => ({
        '@type': 'ListItem', position: index + 1,
        name: item.name[lang], item: absoluteUrl(languagePath(item, lang)),
      })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
}

export default function Seo() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const code = language === 'en' ? 'en' : 'sv';
  const page = findPage(pathname);

  if (!page) {
    return <Helmet htmlAttributes={{ lang: code }}>
      <title>{language === 'en' ? 'Page not found' : 'Sidan hittades inte'} | THAS Multitech</title>
      <meta name="robots" content="noindex, follow" />
    </Helmet>;
  }

  const title = page.title[language];
  const description = page.description[language];
  const url = absoluteUrl(languagePath(page, language));
  const image = absoluteUrl(page.image || DEFAULT_IMAGE);
  const imageAlt = page.id === '3d-printing'
    ? (language === 'en' ? 'Illustration of a 3D printer producing a component' : 'Illustration av en 3D-skrivare som tillverkar en detalj')
    : page.type === 'article' ? page.name[language] : 'THAS Multitech';
  // Escape less-than signs so source content can never end the JSON-LD script element.
  const structuredData = JSON.stringify(createStructuredData(page, language)).replace(/</g, '\\u003c');

  return <Helmet htmlAttributes={{ lang: code }}>
    <title>{title}</title>
    <meta name="description" content={description} />
    <meta name="robots" content="index, follow, max-image-preview:large" />
    <link rel="canonical" href={url} />
    <link rel="alternate" hrefLang="sv" href={absoluteUrl(languagePath(page, 'swe'))} />
    <link rel="alternate" hrefLang="en" href={absoluteUrl(languagePath(page, 'en'))} />
    <link rel="alternate" hrefLang="x-default" href={absoluteUrl(languagePath(page, 'swe'))} />
    <meta property="og:site_name" content="THAS Multitech" />
    <meta property="og:type" content={page.type === 'article' ? 'article' : 'website'} />
    <meta property="og:locale" content={language === 'en' ? 'en_GB' : 'sv_SE'} />
    <meta property="og:locale:alternate" content={language === 'en' ? 'sv_SE' : 'en_GB'} />
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:url" content={url} />
    <meta property="og:image" content={image} />
    <meta property="og:image:alt" content={imageAlt} />
    <meta name="twitter:card" content={page.image ? 'summary_large_image' : 'summary'} />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={image} />
    <meta name="twitter:image:alt" content={imageAlt} />
    <script type="application/ld+json">{structuredData}</script>
  </Helmet>;
}
